import { Auth0DecodedHash, WebAuth } from 'auth0-js'
import axios, { AxiosError, AxiosInstance, AxiosRequestHeaders, InternalAxiosRequestConfig, isAxiosError } from 'axios'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import { Store } from 'redux'

import { appConfig } from '@/app-config/appConfig'
import { isIE } from '@/lib/browser'
import history from '@/lib/history'
import { HTTPMethod } from '@/lib/requests/types'
import { Routes } from '@/lib/routes'
import { dumpQueryString, parseQueryString } from '@/lib/utils'
import { IAction } from '@/redux/actions/action-helpers'
import { Actions as AuthActions } from '@/redux/actions/auth.actions'
import { Actions as RootActions } from '@/redux/actions/root.actions'
import { clearLocalStorage } from '@/redux/reducers/auth.reducer.helpers'
import { ApplicationState } from '@/redux/stores/types'
import { LoginDetails, LoginStatus } from '@/travelsuit'
import { setPermissionsWhenUserLogsIn } from '@/travelsuit/permissions'
import { dispatchUserInfo } from '@/travelsuit/users'

import { calculateExpirationTime } from './auth.utils'
import { storages } from './browserStorage'
import { getRefreshTokenFromStorage } from './getRefreshTokenFromStorage'
import { ImmutableMap } from './immutable/ImmutableMap'
import { loadData } from './requests/loadData'
import { redirectToLogin } from './route-utils'

let tokenTimeout: number
let authProvider: WebAuth

export const getAccessToken = () => storages.local.getItem('access_token')

export function initializeAuthProvider() {
	authProvider = new WebAuth({
		audience: appConfig.AUTH0_AUDIENCE,
		clientID: appConfig.AUTH0_CLIENT_ID,
		domain: appConfig.AUTH0_DOMAIN,
		redirectUri: appConfig.AUTH0_CALLBACK_URL,
		responseType: 'token id_token',
		scope: 'openid profile email user_metadata app_metadata offline_access',
	})
}

let timerModal: NodeJS.Timeout | null = null
let timerSessionLogout: NodeJS.Timeout | null = null
let timerCheckSession: NodeJS.Timeout | null = null

export const getMillisecondsToExpire = (expiresAt: Date) => {
	const timezoneOffset = new Date().getTimezoneOffset()
	const now = moment(new Date()).add(timezoneOffset, 'minutes').utc()

	return moment(expiresAt).diff(now)
}

function setAuthHeader<T extends { headers?: AxiosRequestHeaders }>({
	config,
	accessToken,
}: {
	config: T
	accessToken: string
}) {
	return config.headers
		? {
				...config,
				headers: { ...config.headers, Authorization: `Bearer ${accessToken}` },
			}
		: config
}

function onAuthError(store: Store<ApplicationState, IAction>) {
	clearUserSession(store, false)
	if (!window.location.pathname.includes(Routes.Login)) {
		redirectToLogin()
	}
}

function persistAuthInfoInStorage(authResult: Required<auth0.Auth0DecodedHash>) {
	storages.local.setItem('expires_at', String(calculateExpirationTime(authResult)))
	storages.local.setItem('id_token', authResult.idToken)
	storages.local.setItem('access_token', authResult.accessToken)
	if (authResult.refreshToken) {
		storages.local.setItem('refresh_token', authResult.refreshToken)
	}
}

type AxiosAuthConfig = InternalAxiosRequestConfig & { _authAttempt?: number }

export function createGetGoingAuthRequestInterceptor() {
	return function getGoingAuthRequestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
		const accessToken = getAccessToken()

		return accessToken ? setAuthHeader({ config, accessToken }) : config
	}
}

export function isUnauthorizedError(error: unknown): error is AxiosError {
	return isAxiosError(error) && error.response?.status === 401
}

export function createGetGoingAuthFailedResponseInterceptor(
	getStore: () => Store<ApplicationState, IAction>,
	client: AxiosInstance = axios,
) {
	return async function getGoingAuthFailedResponseInterceptor(error: AxiosError) {
		if (!isUnauthorizedError(error) || !error.config) {
			throw error
		}

		if ('_authAttempt' in error.config) {
			const authAttempt = (error.config as AxiosAuthConfig)._authAttempt
			if (authAttempt && authAttempt >= 3) {
				throw error
			}
		}

		const refreshToken = getRefreshTokenFromStorage()
		const store = getStore()

		if (!refreshToken) {
			onAuthError(store)
			throw error
		}

		const { accessToken } = await waitAccessTokenRefresh(store, refreshToken)
		const retryRequestConfig: AxiosAuthConfig = {
			...error.config,
			headers: { ...error.config.headers } as AxiosRequestHeaders,
			_authAttempt: ((error.config as AxiosAuthConfig)._authAttempt ?? 0) + 1,
		}

		return client.request(setAuthHeader({ config: retryRequestConfig, accessToken }))
	}
}

let exchangeRefreshTokenPromise: Promise<{ accessToken: string }> | undefined = undefined

function waitAccessTokenRefresh(store: Store<ApplicationState, IAction>, refreshToken: string) {
	exchangeRefreshTokenPromise =
		exchangeRefreshTokenPromise ??
		new Promise<{ accessToken: string }>((resolve, reject) => {
			authProvider.client.oauthToken(
				{
					grantType: 'refresh_token',
					refreshToken,
				},
				(err, result) => {
					if (err) {
						onAuthError(store)
						reject(err)
						return
					}

					persistAuthInfoInStorage(result)
					scheduleProviderAuthRenewal(store, calculateExpirationTime(result))

					const accessToken = result.accessToken

					store.dispatch(AuthActions.internalLogin({}, () => resolve({ accessToken }), reject))
				},
			)
		}).finally(() => {
			exchangeRefreshTokenPromise = undefined
		})

	return exchangeRefreshTokenPromise
}

function scheduleAuthSessionInfoRefresh(store: Store<ApplicationState, IAction>) {
	store.dispatch(AuthActions.getSessionInfo(() => setTimerCheckSessionInfo(store)))
}

function getSessionInfoFromStore(store: Store<ApplicationState, IAction>) {
	return store.getState().auth.get('sessionInfo')
}

function setTimerCheckSessionInfo(store: Store<ApplicationState, IAction>) {
	const minPeriod = 30000
	const period = Math.max(
		minPeriod,
		(getMillisecondsToExpire(getSessionInfoFromStore(store)?.expires_at) -
			appConfig.TIME_TO_NOTIFY_SESSION_EXPIRATION) /
			2,
	)

	const refreshToken = getRefreshTokenFromStorage()

	if (refreshToken) {
		timerCheckSession = setTimeout(async () => {
			await waitAccessTokenRefresh(store, refreshToken)
			scheduleAuthSessionInfoRefresh(store)
		}, period)

		return
	}

	setTimerShowModal(store)
	setTimerLogout(store)

	timerCheckSession = setTimeout(async () => {
		scheduleAuthSessionInfoRefresh(store)
	}, period)
}

function setTimerShowModal(store: Store<ApplicationState, IAction>) {
	const period =
		getMillisecondsToExpire(getSessionInfoFromStore(store)?.expires_at) - appConfig.TIME_TO_NOTIFY_SESSION_EXPIRATION

	if (timerModal) {
		clearTimeout(timerModal)
	}

	timerModal = setTimeout(() => {
		if (getSessionInfoFromStore(store)) {
			store.dispatch(AuthActions.showModal())
		}
	}, period)
}

function clearTimers() {
	if (timerModal) {
		clearTimeout(timerModal)
	}
	if (timerSessionLogout) {
		clearTimeout(timerSessionLogout)
	}
	if (timerCheckSession) {
		clearTimeout(timerCheckSession)
	}
}

export function setTimerLogout(store: Store<ApplicationState, IAction>) {
	const showModal = store.getState().auth.get('showModal')
	const period = getMillisecondsToExpire(getSessionInfoFromStore(store)?.expires_at)

	if (timerSessionLogout) {
		clearTimeout(timerSessionLogout)
	}

	timerSessionLogout = setTimeout(() => {
		store.dispatch(
			AuthActions.getSessionInfo((sessionInfo) => {
				if (
					showModal !== null &&
					sessionInfo.expires_at !== showModal &&
					getMillisecondsToExpire(sessionInfo.expires_at) > 0
				) {
					return
				}

				clearTimers()

				if (getMillisecondsToExpire(sessionInfo.expires_at) <= 0) {
					requestProviderLogout(store)
				}
			}),
		)
	}, period)
}

export const getAuthPreloadedState = <T extends ApplicationState['auth']>(
	store: Store<ApplicationState, IAction>,
): T => {
	const idToken = storages.local.getItem('id_token')
	const internalUser = storages.local.getItem('internal_user')
	const termsAccepted = storages.local.getItem('terms_accepted')

	if (internalUser) {
		dispatchUserInfo(JSON.parse(internalUser))
	}

	const expiresAt = Number(storages.local.getItem('expires_at'))
	if (!getRefreshTokenFromStorage() && moment(expiresAt).valueOf() < moment(new Date()).valueOf()) {
		return new ImmutableMap() as T
	}

	scheduleProviderAuthRenewal(store, expiresAt)

	return new ImmutableMap(
		Object.entries({
			user: idToken ? jwtDecode(idToken) : null,
			accessToken: getAccessToken(),
			idToken: storages.local.getItem('id_token'),
			expiresAt: expiresAt || undefined,
			internalUser: internalUser ? JSON.parse(internalUser) : null,
			termsAccepted: termsAccepted ? JSON.parse(termsAccepted) : null,
		}),
	) as T
}

export async function providerLogin({
	store,
	username,
	password,
	shouldUseRefreshToken,
}: {
	store: Store<ApplicationState, IAction>
	username: string
	password: string
	shouldUseRefreshToken?: boolean
}): Promise<LoginDetails> {
	try {
		await clearUserSession(store, false)
		const authResult = await new Promise<Auth0DecodedHash>((resolve, reject) => {
			authProvider.client.login(
				{
					username,
					password,
					realm: appConfig.AUTH0_DB_CONNECTION,
				},
				(err, result) => (err ? reject(err) : resolve(result)),
			)
		})

		if (!shouldUseRefreshToken) {
			delete authResult.refreshToken
		}

		await saveUserSession(store, authResult)
		return await internalLogin({ store })
	} catch (e) {
		clearUserSession(store, true)
		throw e
	}
}

export async function providerPwdReset(
	store: Store<ApplicationState, IAction>,
	email: string,
	clearUserOnFailure = true,
) {
	try {
		const resetResult = await new Promise<string>((resolve, reject) => {
			authProvider.changePassword(
				{
					email,
					connection: appConfig.AUTH0_DB_CONNECTION,
				},
				(err, result) => (err ? reject(err) : resolve(result)),
			)
		})
		return resetResult
	} catch (e) {
		if (clearUserOnFailure) {
			clearUserSession(store, true)
		}
		throw e
	}
}

export async function internalLogin({
	store,
	sso_registration,
}: {
	store: Store<ApplicationState, IAction>
	sso_registration?: boolean
}) {
	return new Promise<LoginDetails>((resolve, reject) => {
		store.dispatch(
			AuthActions.internalLogin(
				{ sso_registration },
				(details) => {
					if (!details) {
						return
					}
					if (details.login_status === LoginStatus.UserExists) {
						setPermissionsWhenUserLogsIn(store)
					}

					scheduleAuthSessionInfoRefresh(store)

					return resolve(details)
				},
				reject,
			),
		)
	})
}

export async function tryHashQueryLogin(store: Store<ApplicationState, IAction>) {
	if (!history.location.hash) {
		return
	}

	const params = new URLSearchParams(history.location.hash.slice(1))
	const returnTo = params.get('state')
	const isTestConnection = returnTo?.includes(Routes.ManageSso)

	const accessTokenValue = params.get('access_token')
	const idTokenValue = params.get('id_token')
	const errorType = params.get('error')
	const errorDescription = params.get('error_description')

	if (!(accessTokenValue && idTokenValue)) {
		if (returnTo?.includes(Routes.ManageSso)) {
			const url = new URL(returnTo, window.location.origin)

			url.searchParams.set('status', 'failed')

			if (errorType) {
				url.searchParams.set('error', errorType)
			}

			if (errorDescription) {
				url.searchParams.set('error_description', errorDescription)
			}

			history.replace(`${url.pathname}${url.search}`)
		}
		return
	}

	try {
		const accessToken = jwtDecode<{ exp: number; sub: string }>(accessTokenValue)
		jwtDecode(idTokenValue)

		const expiresIn = accessToken ? Math.floor((accessToken.exp * 1000 - Date.now()) / 1000) : 0
		if (expiresIn > 0) {
			params.delete('access_token')
			params.delete('id_token')

			storages.local.removeItem('refresh_token')
			const sso_registration = accessToken.sub?.startsWith('samlp|') || false
			history.replace(`#${params.toString()}`)

			await saveUserSession(store, { accessToken: accessTokenValue, idToken: idTokenValue, expiresIn })
			await internalLogin({
				store,
				sso_registration,
			})
			if (returnTo) {
				if (isTestConnection) {
					const url = new URL(returnTo, window.location.origin)
					url.searchParams.set('status', 'success')
					history.replace(`${url.pathname}${url.search}`)
				} else {
					history.replace(returnTo)
				}
			}
		}
	} catch (err) {
		console.error('tryHashQueryLogin failed', err)
	}
}

export async function acceptTermsOfService(store: Store<ApplicationState, IAction>) {
	const { data } = await loadData<LoginDetails>({ method: HTTPMethod.POST, resourcePath: 'accept_tos' })

	if (data.login_status === LoginStatus.UserExists) {
		const { user, ...rest } = data
		store.dispatch(AuthActions.setInternalUser(user, rest))
		store.dispatch(AuthActions.setTermsAccepted(true))
		setPermissionsWhenUserLogsIn(store)
	}
}

export function requestProviderLogout(store: Store<ApplicationState, IAction>) {
	clearTimers()

	loadData({ method: HTTPMethod.POST, resourcePath: 'logout' }).then(() => {
		if (store.getState().auth.get('user')) {
			authProvider.logout({
				returnTo: `${window.location.protocol}//${window.location.host}/logout`,
			})

			clearUserSession(store, false)
		}
	})
}

function renewProviderAuth(store: Store<ApplicationState, IAction>) {
	authProvider.checkSession({}, (err, result) => {
		if (!err) {
			return saveUserSession(store, result)
		}

		console.warn(err)
		return clearUserSession(store, true)
	})
}

function scheduleProviderAuthRenewal(store: Store<ApplicationState, IAction>, expiresAt: number) {
	const delay = (expiresAt || Date.now() - 1) - Date.now()

	if (delay <= 0) {
		return
	}

	window.clearTimeout(tokenTimeout)
	tokenTimeout = window.setTimeout(() => renewProviderAuth(store), delay)
}

async function saveUserSession(store: Store<ApplicationState, IAction>, authResult: Auth0DecodedHash) {
	scheduleProviderAuthRenewal(store, calculateExpirationTime(authResult as Required<Auth0DecodedHash>))
	persistAuthInfoInStorage(authResult as Required<Auth0DecodedHash>)
	return new Promise((resolve) => {
		store.dispatch(
			AuthActions.finishLogin(authResult, () => {
				return resolve(authResult)
			}),
		)
	})
}

async function clearUserSession(store: Store<ApplicationState, IAction>, fail: boolean) {
	if (fail) {
		store.dispatch(AuthActions.failLogin())
	}
	clearLocalStorage()
	clearTimeout(tokenTimeout)
	store.dispatch(
		RootActions.clearStore({
			auth: getAuthPreloadedState(store),
		}),
	)
}

export async function handleUserReloadRequest(store: Store<ApplicationState, IAction>) {
	const qs = parseQueryString(history.location.search)
	if (qs.reloadUser) {
		try {
			await internalLogin({ store })
			const newQs = dumpQueryString({ ...qs, reloadUser: undefined })
			const hash = history.location.hash
			const trail = `${newQs ? '?' + newQs : ''}${hash ? '#' + hash : ''}`
			history.replace(history.location.pathname + trail)
		} catch (err) {
			console.error('handleUserReloadRequest', err)
		}
	}
}

export function handleIE(store: Store<ApplicationState, IAction>) {
	if (isIE) {
		requestProviderLogout(store)
	}
}

export function providerLoginSSO(
	store: Store<ApplicationState, IAction>,
	{
		loginHint,
		returnTo,
		connection,
	}: {
		loginHint?: string
		connection?: string
		returnTo?: string
	} = {},
) {
	clearUserSession(store, false)

	authProvider.authorize({
		login_hint: loginHint,
		state: returnTo ?? Routes.Home,
		connection,
	})
}
